import type {
  EntityPermissionScope,
  ModulePermissionScope,
  OrganizationWithChildren,
  PermissionA,
  PermissionACRUD,
  PermissionCRUDH,
  PermissionGrant,
  PermissionRUD,
  TemplateListBody,
  TemplatePermissionScope,
  TenantWithChildren,
} from "@scayla/api-client-fetch";
import {
  ComparisonOperator,
  FiltersetIndividualPermissionFieldId,
  organizationTreeForTenant,
} from "@scayla/api-client-fetch";
import {
  filtersetIndividualPermissionListOptions,
  forMeOptions,
  modulesOptions,
  templatesOptions,
  tenantsForMeOptions,
  userMeOptions,
} from "@scayla/api-client-fetch/vue-query";
import { Sentry, useAuth, useLogger } from "@scayla/core";
import { useQueries, useQuery, useQueryClient } from "@tanstack/vue-query";
import { until } from "@vueuse/core";
import { defineStore } from "pinia";
import { computed, ref, watch, watchEffect } from "vue";

import { $t } from "@/i18n";
import { useUserSettingsStore } from "@/store/userSettings.ts";
import { BUILTIN_MODULE_SORT_ORDER } from "@/utils/moduleUtils";
import { showToastForError, showToastForErrorRef } from "@/utils/toast";

// Custom types for flattened permissions with grantBy as array
type FlattenedTemplatePermissionScope = Omit<TemplatePermissionScope, "grantBy"> & {
  grantBy: PermissionGrant[];
};

type FlattenedEntityPermissionScope = Omit<EntityPermissionScope, "grantBy"> & {
  grantBy: PermissionGrant[];
};

type FlattenedModulePermissionScope = Omit<ModulePermissionScope, "grantBy"> & {
  grantBy: PermissionGrant[];
};

function flattenOrganizationChildren(items: OrganizationWithChildren[]): OrganizationWithChildren[] {
  const result: OrganizationWithChildren[] = [];

  for (const item of items) {
    result.push(item);
    if ("children" in item && item.children.length > 0) {
      result.push(...flattenOrganizationChildren(item.children));
    }
  }

  return result;
}

export const useWorkspace = defineStore(
  "workspace",
  () => {
    const logger = useLogger("store/workspace");
    const auth = useAuth();
    const userSettingsStore = useUserSettingsStore();

    const tenantId = ref<string | undefined>(undefined);

    const userMeQueryOptions = computed(() => ({
      ...userMeOptions({ query: { tenantId: tenantId.value } }),
    }));

    const {
      data: user,
      isLoadingError: userHasError,
      error: userError,
      isLoading: userLoading,
      refetch: userRefetch,
    } = useQuery(userMeQueryOptions);

    logger.debug("Loading User", user)();

    if (userHasError.value) {
      showToastForErrorRef(userError);
    }

    watch(userError, async () => {
      if (!userError.value) {
        return;
      }
      if ("statusCode" in userError.value) {
        {
          if (userError.value.statusCode !== "401") {
            logger.debug("Loading user is not possible:", userError.value.message)();
            clearStorage();
            await auth.signoutRedirect();
            return;
          }
        }
      }
      logger.debug("Loading user is not possible:", userError.value.message)();
    });

    watchEffect(() => {
      if (!user.value) {
        logger.debug("Resetting user for Sentry...");
        Sentry.setUser(null);
      } else {
        logger.debug("Setting user for Sentry...");
        Sentry.setUser({
          email: user.value.email,
          id: user.value.id,
          name: user.value.name,
        });
      }
    });

    const {
      data: permissions,
      isLoadingError: permissionsHasError,
      error: permissionsError,
      isLoading: permissionsLoading,
      refetch: permissionsRefetch,
    } = useQuery({
      ...forMeOptions(),
    });

    logger.debug("Loading Permissions", permissions)();

    if (permissionsHasError.value) {
      showToastForErrorRef(permissionsError);
    }

    watch(permissionsError, async () => {
      if (!permissionsError.value) {
        return;
      }
      if ("statusCode" in permissionsError.value) {
        {
          if (permissionsError.value.statusCode !== "401") {
            logger.debug("Loading permissions is not possible:", permissionsError.value.message)();
            clearStorage();
            await auth.signoutRedirect();
            return;
          }
        }
      }
      logger.debug("Loading permissions is not possible:", permissionsError.value.message)();
    });

    const filtersetPermissionsQueryOptions = computed(() => ({
      ...filtersetIndividualPermissionListOptions({
        body: {
          filter: {
            kind: "condition" as const,
            field: FiltersetIndividualPermissionFieldId.FILTERSET_INDIVIDUAL_PERMISSION_USER_ID,
            operator: ComparisonOperator.EQ,
            value: user.value?.id ?? "",
          },
        },
      }),
      enabled: !!user.value?.id,
    }));

    const {
      data: filtersetIndividualPermissionsData,
      error: filtersetPermissionsError,
      refetch: filtersetPermissionsRefetch,
    } = useQuery(filtersetPermissionsQueryOptions);
    showToastForErrorRef(filtersetPermissionsError);

    const {
      data: tenants,
      isLoadingError: tenantsHasError,
      error: tenantsError,
      isLoading: tenantsLoading,
      refetch: tenantsRefetch,
    } = useQuery(tenantsForMeOptions());

    function reportInvalidTenant(activeTenantId: string) {
      showToastForError($t("common.label.notFound"));
      const url = new URL(window.location.href);
      url.searchParams.set("tenant", activeTenantId);
      window.history.replaceState(history.state, "", url.toString());
    }

    const hasInitializedTenant = ref(false);
    // Set when the current tenant disappears from tenantsForMe mid-session (deleted elsewhere); the layout
    // reacts by opening Home of the fallback tenant.
    const removedTenantId = ref<string | undefined>(undefined);

    watch(
      [tenants, () => userSettingsStore.defaultTenant, () => userSettingsStore.globalUserSettingsLoading],
      async ([newTenants, defaultTenantId, globalUserSettingsLoading]) => {
        if (!newTenants || newTenants.length === 0) {
          return;
        }

        if (globalUserSettingsLoading) {
          return;
        }

        const currentTenantIsValid = tenantId.value !== undefined && newTenants.some((t) => t.id === tenantId.value);

        if (hasInitializedTenant.value && currentTenantIsValid) {
          return;
        }

        const invalidTenantId = tenantId.value !== undefined && !currentTenantIsValid ? tenantId.value : undefined;
        const removedDuringSession = hasInitializedTenant.value && invalidTenantId !== undefined;

        if (currentTenantIsValid && tenantId.value) {
          hasInitializedTenant.value = true;
          if (defaultTenantId !== tenantId.value) {
            await userSettingsStore.setDefaultTenant(tenantId.value);
          }
          return;
        }

        // tenantsForMe also lists tenants the user has no organization in; prefer one they can open. Before
        // the org trees load nothing is selectable yet, so every listed tenant is a candidate.
        const selectableIds = selectableTenants.value?.map((t) => t.id) ?? [];
        const candidateIds = selectableIds.length > 0 ? selectableIds : newTenants.map((t) => t.id);
        const fallbackTenantId =
          defaultTenantId && candidateIds.includes(defaultTenantId) ? defaultTenantId : candidateIds[0];

        tenantId.value = fallbackTenantId;
        hasInitializedTenant.value = true;

        if (removedDuringSession) {
          removedTenantId.value = invalidTenantId;
        } else if (invalidTenantId) {
          reportInvalidTenant(fallbackTenantId);
        }

        if (defaultTenantId !== fallbackTenantId) {
          await userSettingsStore.setDefaultTenant(fallbackTenantId);
        }
      },
      { immediate: false },
    );

    logger.debug("Loading Tenants", tenants)();

    if (tenantsHasError.value) {
      showToastForErrorRef(tenantsError);
    }

    const tenantOrgTreeQueries = computed(() => {
      return (tenants.value ?? []).map((tenant) => ({
        queryKey: ["organizationTreeForTenant", tenant.id],
        queryFn: async () => {
          const response = await organizationTreeForTenant({
            query: { tenantId: tenant.id },
          });
          return response.data ?? null;
        },
        enabled: !!tenants.value?.length,
      }));
    });

    const tenantOrgTreeResults = useQueries({ queries: tenantOrgTreeQueries });

    const tenantOrgTrees = computed(() => {
      const map = new Map<string, TenantWithChildren>();
      // Keyed by the tree's own tenant id: right after tenants changes, the results can still be in the
      // previous list's order.
      tenantOrgTreeResults.value.forEach((result) => {
        if (result.data && !result.isError) {
          map.set(result.data.id, result.data);
        }
      });
      return map;
    });

    const allOrgTreesFlat = computed(() => {
      const flat: OrganizationWithChildren[] = [];
      tenantOrgTrees.value.forEach((tenantTree) => {
        if (tenantTree.children) {
          flat.push(...flattenOrganizationChildren(tenantTree.children));
        }
      });
      return flat;
    });

    const currentOrgTree = computed(() =>
      tenantId.value ? (tenantOrgTrees.value.get(tenantId.value)?.children ?? []) : [],
    );

    const orgTreesLoading = computed(() => tenantOrgTreeResults.value.some((q) => q.isLoading));

    // tenantsForMe also lists tenants where the user has no organization; those can't be picked.
    const selectableTenants = computed(() =>
      tenants.value?.filter((t) => {
        const children = tenantOrgTrees.value.get(t.id)?.children ?? [];
        if (children.length > 0) return true;
        if (!tenantOrgTrees.value.has(t.id) || !t.organization) return false;
        return !!t.organization.name?.trim();
      }),
    );

    // isLoading is false for a moment before a just-enabled query starts, so wait for every tree to settle.
    const selectableTenantsSettled = computed(
      () =>
        tenants.value !== undefined &&
        tenantOrgTreeResults.value.length === tenants.value.length &&
        tenantOrgTreeResults.value.every((q) => !q.isPending),
    );

    /** Never rejects: on timeout it resolves with whatever is loaded. */
    async function waitForSelectableTenants(timeoutMs = 10000): Promise<void> {
      await until(selectableTenantsSettled).toBe(true, { timeout: timeoutMs });
    }
    const orgTreesHasError = computed(() => tenantOrgTreeResults.value.some((q) => q.isError));

    const tenant = computed(() => tenants.value?.find((t) => t.id === tenantId.value));

    const hasMultipleRootOrgs = computed(() => {
      const tree = tenantOrgTrees.value.get(tenantId.value ?? "");
      return (tree?.children?.length ?? 0) > 1;
    });

    const shouldShowTenant = computed(() => hasMultipleRootOrgs.value);

    const orgId = ref<string | undefined>(undefined);

    const previousTenantId = ref<string | undefined>(tenantId.value);
    // Set by setTenantAndOrg right before it commits both values, so this watcher (which
    // otherwise clears orgId whenever tenantId changes) doesn't wipe the org that was just
    // explicitly picked together with the tenant.
    const explicitOrgPick = ref(false);
    watch(
      [tenantId, tenant, shouldShowTenant, tenantOrgTrees],
      ([newTenantId, newTenant, showTenant]) => {
        if (newTenantId && newTenant) {
          const tree = tenantOrgTrees.value.get(newTenantId);
          if (!tree?.children || tree.children.length === 0) {
            return;
          }

          const tenantChanged = newTenantId !== previousTenantId.value;
          const wasPreviousTenantId = previousTenantId.value;
          previousTenantId.value = newTenantId;

          if (explicitOrgPick.value) {
            explicitOrgPick.value = false;
            return;
          }

          if (showTenant) {
            if (tenantChanged && wasPreviousTenantId !== undefined && orgId.value) {
              logger.debug("Clearing orgId for multiple root organizations on tenant change")();
              orgId.value = undefined;
            }
            return;
          }
        }
      },
      { immediate: false },
    );

    const orgTree = computed(() => currentOrgTree.value);

    const orgTreeFlat = ref<OrganizationWithChildren[]>([]);
    watchEffect(() => (orgTreeFlat.value = orgTree.value ? flattenOrganizationChildren(orgTree.value) : []));

    // Handle invalid persisted orgId: if orgId is set but not present in the current flattened org tree,
    // reset it to the first available org in the tree.
    watchEffect(() => {
      if (orgTreeFlat.value.length > 0 && orgId.value) {
        const isValid = orgTreeFlat.value.some((o) => o.id === orgId.value);
        if (!isValid) {
          orgId.value = orgTreeFlat.value[0].id;
          logger.debug("Setting default orgId due to invalid persisted one", orgTreeFlat.value[0].id)();
        }
      }
    });

    const org = computed(() => {
      if (!tenantId.value || !orgId.value) return null;

      return orgTreeFlat.value.find((o) => o.id === orgId.value) ?? null;
    });

    const effectiveOrganizationId = computed(() => {
      if (orgId.value) {
        return orgId.value;
      }

      return tenant.value?.organization?.id;
    });

    const modulesOptionsComputed = computed(() => ({
      ...modulesOptions({
        query: {
          tenantId: tenantId.value,
          organizationId: orgId.value ?? undefined,
        },
      }),
      enabled: !!tenantId.value,
    }));

    const {
      data: unfilteredModules,
      isLoadingError: modulesHasError,
      error: modulesError,
      isLoading: modulesLoading,
      refetch: modulesRefetch,
    } = useQuery(modulesOptionsComputed);
    if (modulesHasError.value) {
      showToastForErrorRef(modulesError);
    }

    const modules = computed(() => {
      let filteredModules = unfilteredModules.value;
      if (!tenant.value?.accountingEnabled) {
        filteredModules = unfilteredModules.value?.filter((m) => m.id !== "9c9769bd-1e3a-41dd-9fe7-51260f415b70");
      }
      // ordering of modules
      filteredModules = [...(filteredModules ?? [])].sort((a, b) => {
        return BUILTIN_MODULE_SORT_ORDER.indexOf(a.builtinModule) - BUILTIN_MODULE_SORT_ORDER.indexOf(b.builtinModule);
      });

      return filteredModules;
    });

    // List of modules that can be displayed
    const displayModules = computed(() => {
      return modules.value?.filter((m) => !m.settingsModule);
    });

    const accountingSettingsTemplatesOptions = computed(() => ({
      ...templatesOptions({
        body: {
          tenantId: tenantId.value,
          organizationId: orgId.value ?? undefined,
          moduleId: "974f96ca-7819-46a7-be9d-c00706e69828",
        } as unknown as TemplateListBody,
      }),
      enabled: !!tenantId.value,
    }));

    const { data: accountingSettingsTemplates, error: templatesError } = useQuery(accountingSettingsTemplatesOptions);
    showToastForErrorRef(templatesError);

    // Utility

    async function workspaceRefetchAll() {
      await userRefetch();
      await permissionsRefetch();
      await tenantsRefetch();
      await modulesRefetch();
      await filtersetPermissionsRefetch();
      await Promise.all(tenantOrgTreeResults.value.map((q) => q.refetch()));
    }

    const workspaceLoading = computed(() => {
      return (
        userLoading.value ||
        permissionsLoading.value ||
        tenantsLoading.value ||
        orgTreesLoading.value ||
        modulesLoading.value
      );
    });

    const hasEffectiveOrg = computed(() => !!orgId.value || !!tenant.value?.organization?.id);

    const workspaceReady = computed(() => {
      return (
        !workspaceLoading.value &&
        !!tenantId.value &&
        hasEffectiveOrg.value &&
        orgTree.value !== undefined &&
        modules.value !== undefined
      );
    });

    const permissionsReady = computed(() => !!permissions.value && hasEffectiveOrg.value);

    const workspaceHasErrors = computed(() => {
      return (
        userHasError.value ||
        permissionsHasError.value ||
        tenantsHasError.value ||
        orgTreesHasError.value ||
        modulesHasError.value
      );
    });

    /**
     * Resolves once the /permissions/forMe payload is in, so a caller that reads
     * getEntityPermissions outside a component — the router guard — does not read the all-false
     * defaults while the query is still in flight. tenantId is restored from localStorage, so
     * waitForTenantId alone returns long before permissions exist.
     *
     * Never rejects: on timeout it resolves and lets the caller fall through to those defaults,
     * which for a permission check is the safe answer.
     */
    async function waitForPermissions(timeoutMs = 10000): Promise<void> {
      if (permissions.value) return;

      return new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
          unwatchPermissions();
          resolve();
        }, timeoutMs);

        const unwatchPermissions = watch(
          permissions,
          (newPermissions) => {
            if (newPermissions) {
              clearTimeout(timeoutId);
              unwatchPermissions();
              resolve();
            }
          },
          { flush: "sync" },
        );
      });
    }

    async function waitForTenantId(timeoutMs = 10000): Promise<string> {
      if (tenantId.value) return tenantId.value;

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          unwatchTenant();
          reject(new Error("TenantId loading timed out"));
        }, timeoutMs);

        const unwatchTenant = watch(
          tenantId,
          (newTenantId) => {
            if (newTenantId) {
              clearTimeout(timeoutId);
              unwatchTenant();
              resolve(newTenantId);
            }
          },
          { flush: "sync" },
        );
      });
    }

    // Computed flat template permissions for better performance
    const flatTemplatePermissions = computed((): Map<string, FlattenedTemplatePermissionScope> => {
      if (!permissions.value?.scope) {
        return new Map();
      }

      // Flatten all template permissions from all organizations and create a Map for O(1) lookup
      // Merge permissions: if one is true, it's always true; merge grantby as array
      const permissionsMap = new Map();
      permissions.value.scope.forEach((orgScope) => {
        orgScope.templates.forEach((templatePermission) => {
          const templateId = templatePermission.template.id;
          const existing = permissionsMap.get(templateId);

          if (existing) {
            // Merge permissions - if any permission is true, keep it true
            const mergedPermission = {
              ...existing,
              permission: {
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                create: existing.permission.create || templatePermission.permission.create,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                read: existing.permission.read || templatePermission.permission.read,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                edit: existing.permission.edit || templatePermission.permission.edit,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                delete: existing.permission.delete || templatePermission.permission.delete,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                handover: existing.permission.handover || templatePermission.permission.handover,
              },
              // Merge grantBy - collect all possible origins into an array
              grantBy: [...existing.grantBy, templatePermission.grantBy].filter(
                (grant, index, self) =>
                  // Remove duplicates based on type and name
                  index === self.findIndex((g) => g.type === grant.type && g.name === grant.name),
              ),
            };
            permissionsMap.set(templateId, mergedPermission);
          } else {
            // First occurrence, wrap grantBy in an array
            permissionsMap.set(templateId, {
              ...templatePermission,
              grantBy: [templatePermission.grantBy],
            });
          }
        });
      });

      return permissionsMap;
    });

    const permissionOrgId = computed(() => {
      if (orgId.value) return orgId.value;
      return tenant.value?.organization?.id;
    });

    // Computed flat entity permissions for better performance
    const flatEntityPermissions = computed((): Map<string, FlattenedEntityPermissionScope> => {
      if (!permissions.value?.scope) {
        return new Map();
      }

      // Flatten all entity permissions from all organizations and create a Map for O(1) lookup
      // Merge permissions: if one is true, it's always true; merge grantBy as array
      const permissionsMap = new Map();
      permissions.value.scope.forEach((orgScope) => {
        orgScope.entities.forEach((entityPermission) => {
          const entityTableName = entityPermission.entity.table_name;
          const existing = permissionsMap.get(entityTableName);

          if (existing) {
            // Merge permissions - if any permission is true, keep it true
            const mergedPermission = {
              ...existing,
              permission: {
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                active: existing.permission.active || entityPermission.permission.active,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                create: existing.permission.create || entityPermission.permission.create,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                read: existing.permission.read || entityPermission.permission.read,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                edit: existing.permission.edit || entityPermission.permission.edit,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                delete: existing.permission.delete || entityPermission.permission.delete,
              },
              // Merge grantBy - collect all possible origins into an array
              grantBy: [...existing.grantBy, entityPermission.grantBy].filter(
                (grant, index, self) =>
                  // Remove duplicates based on type and name
                  index === self.findIndex((g) => g.type === grant.type && g.name === grant.name),
              ),
            };
            permissionsMap.set(entityTableName, mergedPermission);
          } else {
            // First occurrence, wrap grantBy in an array
            permissionsMap.set(entityTableName, {
              ...entityPermission,
              grantBy: [entityPermission.grantBy],
            });
          }
        });
      });

      return permissionsMap;
    });

    // Computed flat module permissions for better performance
    const flatModulePermissions = computed((): Map<string, FlattenedModulePermissionScope> => {
      if (!permissions.value?.scope) {
        return new Map();
      }

      // Flatten all module permissions from all organizations and create a Map for O(1) lookup
      // Merge permissions: if one is true, it's always true; merge grantBy as array
      const permissionsMap = new Map();
      permissions.value.scope.forEach((orgScope) => {
        orgScope.modules.forEach((modulePermission) => {
          const moduleId = modulePermission.module.id;
          const existing = permissionsMap.get(moduleId);

          if (existing) {
            // Merge permissions - if any permission is true, keep it true
            const mergedPermission = {
              ...existing,
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              active: existing.active || modulePermission.active,
              // Merge grantBy - collect all possible origins into an array
              grantBy: [...existing.grantBy, modulePermission.grantBy].filter(
                (grant, index, self) =>
                  // Remove duplicates based on type and name
                  index === self.findIndex((g) => g.type === grant.type && g.name === grant.name),
              ),
            };
            permissionsMap.set(moduleId, mergedPermission);
          } else {
            // First occurrence, wrap grantBy in an array
            permissionsMap.set(moduleId, {
              ...modulePermission,
              grantBy: [modulePermission.grantBy],
            });
          }
        });
      });

      return permissionsMap;
    });

    // Flat map: filtersetId → PermissionRUD (individual grants for the current user)
    const flatFiltersetPermissions = computed((): Map<string, PermissionRUD> => {
      const map = new Map<string, PermissionRUD>();
      filtersetIndividualPermissionsData.value?.items.forEach((p) => {
        map.set(p.filtersetId, p.permissions);
      });
      return map;
    });

    // Performance optimized permission cache to avoid repeated computations
    const templatePermissionCache = ref<
      Map<string, { read: boolean; create: boolean; edit: boolean; delete: boolean; handover: boolean }>
    >(new Map());
    const entityPermissionCache = ref<
      Map<string, { active: boolean; create: boolean; read: boolean; edit: boolean; delete: boolean }>
    >(new Map());
    const modulePermissionCache = ref<Map<string, { active: boolean }>>(new Map());
    const filtersetPermissionCache = ref<Map<string, PermissionRUD | null>>(new Map());

    // Clear all caches when permissions change
    watchEffect(() => {
      if (permissions.value) {
        templatePermissionCache.value.clear();
        entityPermissionCache.value.clear();
        modulePermissionCache.value.clear();
      }
    });

    watchEffect(() => {
      if (filtersetIndividualPermissionsData.value) {
        filtersetPermissionCache.value.clear();
      }
    });
    // Entity permissions depend on permissionOrgId; clear cache when it changes (e.g. after reload)
    watch(permissionOrgId, () => {
      entityPermissionCache.value.clear();
    });

    enum DefaultPermissionsType {
      TEMPLATE_PERMISSIONS = "PermissionCRUDH",
      ENTITY_PERMISSIONS = "PermissionACRUD",
      MODULE_PERMISSIONS = "PermissionA",
      FILTERSET_PERMISSIONS = "PermissionRUD",
    }

    function getDefaultPermissions(type: DefaultPermissionsType.TEMPLATE_PERMISSIONS): PermissionCRUDH;
    function getDefaultPermissions(type: DefaultPermissionsType.ENTITY_PERMISSIONS): PermissionACRUD;
    function getDefaultPermissions(type: DefaultPermissionsType.MODULE_PERMISSIONS): PermissionA;
    function getDefaultPermissions(type: DefaultPermissionsType.FILTERSET_PERMISSIONS): PermissionRUD;
    function getDefaultPermissions(type: DefaultPermissionsType) {
      switch (type) {
        case DefaultPermissionsType.TEMPLATE_PERMISSIONS:
          return { read: false, create: false, edit: false, delete: false, handover: false };
        case DefaultPermissionsType.ENTITY_PERMISSIONS:
          return { active: false, create: false, read: false, edit: false, delete: false };
        case DefaultPermissionsType.MODULE_PERMISSIONS:
          return { active: false };
        case DefaultPermissionsType.FILTERSET_PERMISSIONS:
          return { read: false, edit: false, delete: false };
      }
    }

    // Optimized utility function to get template permission object with caching
    function getTemplatePermissions(templateId: string | undefined | null): PermissionCRUDH {
      const defaultPermissions = getDefaultPermissions(DefaultPermissionsType.TEMPLATE_PERMISSIONS);
      if (!templateId) return defaultPermissions;

      // Check cache first
      if (templatePermissionCache.value.has(templateId)) {
        return templatePermissionCache.value.get(templateId)!;
      }

      // Compute and cache the result
      const permission = flatTemplatePermissions.value.get(templateId);
      const result = permission ? permission.permission : defaultPermissions;
      templatePermissionCache.value.set(templateId, result);
      return result;
    }

    // Optimized utility function to get entity permission object with caching
    function getEntityPermissions(entityTableName: string | undefined | null): PermissionACRUD {
      const defaultPermissions = getDefaultPermissions(DefaultPermissionsType.ENTITY_PERMISSIONS);
      if (!entityTableName) return defaultPermissions;

      // Check cache first
      if (entityPermissionCache.value.has(entityTableName)) {
        return entityPermissionCache.value.get(entityTableName)!;
      }

      // Compute and cache the result
      const permission = flatEntityPermissions.value.get(entityTableName);
      const result = permission ? permission.permission : defaultPermissions;
      entityPermissionCache.value.set(entityTableName, result);
      return result;
    }

    /**
     * Returns true if the entity permission has at least one grantBy with type === "admin".
     * Used to show/hide features that should be visible only when granted by admin (e.g. Service Integration).
     */
    function isEntityGrantedByAdmin(entityTableName: string | undefined | null): boolean {
      if (!entityTableName) return false;
      const scope = flatEntityPermissions.value.get(entityTableName);
      const grantBy = scope?.grantBy;
      if (!Array.isArray(grantBy) || grantBy.length === 0) return false;
      return grantBy.some((g) => (g.type ?? "").toLowerCase() === "admin");
    }

    // Optimized utility function to get module permission with caching
    function getModulePermissions(moduleId: string | undefined | null) {
      const defaultPermissions = getDefaultPermissions(DefaultPermissionsType.MODULE_PERMISSIONS);
      if (!moduleId) return defaultPermissions;

      // Compute and cache the result
      const permission = flatModulePermissions.value.get(moduleId);
      const result = permission ?? defaultPermissions;
      modulePermissionCache.value.set(moduleId, result);
      return result;
    }

    const queryClient = useQueryClient();

    async function refreshCurrentOrgTree(tenantIdToRefresh: string) {
      const response = await organizationTreeForTenant({ query: { tenantId: tenantIdToRefresh } });
      if (response.data) {
        queryClient.setQueryData(["organizationTreeForTenant", tenantIdToRefresh], response.data);
      }
    }

    // Returns the individual PermissionRUD for the current user on a given filterset,
    // or null when no individual grant exists (caller decides based on FilterPermissionType).
    function getFiltersetPermissions(filtersetId: string | undefined | null) {
      if (!filtersetId) return null;
      if (filtersetPermissionCache.value.has(filtersetId)) {
        return filtersetPermissionCache.value.get(filtersetId) ?? null;
      }
      const result = flatFiltersetPermissions.value.get(filtersetId) ?? null;
      filtersetPermissionCache.value.set(filtersetId, result);
      return result;
    }

    function setTenantId(newTenantId: string) {
      const tenantChanged = tenantId.value !== newTenantId;

      tenantId.value = newTenantId;

      if (!tenantChanged) {
        return;
      }

      hasInitializedTenant.value = true;
      void userSettingsStore.setDefaultTenant(newTenantId);
    }

    // Sets tenant and org together so the org-clearing watcher below (which reacts to
    // tenantId changes and wipes a stale orgId from the previous tenant) doesn't discard
    // the org being picked in the same action.
    function setTenantAndOrg(newTenantId: string, newOrgId: string | undefined) {
      const tenantChanged = tenantId.value !== newTenantId;

      explicitOrgPick.value = true;
      tenantId.value = newTenantId;
      orgId.value = newOrgId;

      if (!tenantChanged) {
        explicitOrgPick.value = false;
        return;
      }

      hasInitializedTenant.value = true;
      void userSettingsStore.setDefaultTenant(newTenantId);
    }

    function clearStorage() {
      tenantId.value = undefined;
      orgId.value = undefined;
      hasInitializedTenant.value = false;
    }

    return {
      // State
      user,
      userHasError,
      userError,
      userLoading,
      hasMultipleRootOrgs,
      shouldShowTenant,
      permissions,
      permissionsHasError,
      permissionsError,
      permissionsLoading,
      tenants,
      tenantsHasError,
      tenantsError,
      tenantsLoading,
      tenant,
      tenantId,
      hasInitializedTenant,
      orgId,
      org,
      effectiveOrganizationId,
      orgTree,
      orgTreeFlat,
      tenantOrgTrees,
      selectableTenants,
      removedTenantId,
      currentOrgTree,
      allOrgTreesFlat,
      orgTreesLoading,
      orgTreesHasError,
      modules,
      displayModules,
      modulesHasError,
      modulesError,
      modulesLoading,
      workspaceLoading,
      accountingSettingsTemplates,
      // Computed
      flatTemplatePermissions,
      flatEntityPermissions,
      flatModulePermissions,
      flatFiltersetPermissions,
      workspaceReady,
      permissionsReady,
      workspaceHasErrors,
      // Actions
      userRefetch,
      permissionsRefetch,
      tenantsRefetch,
      modulesRefetch,
      workspaceRefetchAll,
      getTemplatePermissions,
      getEntityPermissions,
      isEntityGrantedByAdmin,
      getModulePermissions,
      getFiltersetPermissions,
      filtersetPermissionsRefetch,
      getDefaultPermissions,
      setTenantId,
      setTenantAndOrg,
      refreshCurrentOrgTree,
      clearStorage,
      // Types
      DefaultPermissionsType,
      // Promises
      waitForTenantId,
      waitForPermissions,
      waitForSelectableTenants,
    };
  },
  {
    persist: {
      storage: localStorage,
      pick: ["tenantId", "orgId"],
    },
  },
);
